import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Mail, Building2, Trash2 } from "lucide-react";

interface Props {
  ideaId: string;
}

const STATUS_META: Record<string, string> = {
  new: "bg-primary/15 text-primary border border-primary/30",
  replied: "bg-secondary text-secondary-foreground",
  closed: "bg-muted text-muted-foreground",
};

export default function IdeaInboxTab({ ideaId }: Props) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["idea-inbox", ideaId],
    queryFn: async () => {
      const { data: responses, error } = await supabase
        .from("idea_ask_responses")
        .select("*")
        .eq("idea_id", ideaId)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const askIds = Array.from(new Set((responses ?? []).map((r) => r.ask_id)));
      let asksById: Record<string, any> = {};
      if (askIds.length) {
        const { data: asks } = await supabase
          .from("idea_asks")
          .select("id, kind, headline")
          .in("id", askIds);
        asksById = Object.fromEntries((asks ?? []).map((a) => [a.id, a]));
      }
      return (responses ?? []).map((r) => ({ ...r, ask: asksById[r.ask_id] }));
    },
  });

  const setStatus = async (id: string, status: string) => {
    const { error } = await supabase
      .from("idea_ask_responses")
      .update({ status })
      .eq("id", id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ["idea-inbox", ideaId] });
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("idea_ask_responses").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Removed");
    qc.invalidateQueries({ queryKey: ["idea-inbox", ideaId] });
  };

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;

  if (!data || data.length === 0) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        No responses yet. When supporters reach out through your asks, they'll land here.
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {data.map((r: any) => (
        <Card key={r.id} className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-foreground">{r.name}</span>
                {r.ask && (
                  <Badge variant="outline" className="text-xs">
                    {r.ask.headline}
                  </Badge>
                )}
                <Badge className={STATUS_META[r.status] ?? STATUS_META.new}>
                  {r.status}
                </Badge>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-primary">
                  <Mail className="h-3 w-3" /> {r.email}
                </a>
                {r.organization && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-3 w-3" /> {r.organization}
                  </span>
                )}
                <span>{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="replied">Replied</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="icon" onClick={() => remove(r.id)}>
                <Trash2 className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
            {r.message}
          </p>
        </Card>
      ))}
    </div>
  );
}